import { Link } from 'react-router-dom'
import { KNOWN_PLANS, formatEur, planPriceCents, type AdminWorkshopDTO, type WorkshopStatus } from '@wf/shared'
import { startImpersonation } from '../app/impersonation'
import { InfoTip } from '../ui/InfoTip'
import { ChefActivationReveal } from './ChefActivationReveal'
import { STATUS_CHIP, STATUS_LABELS, STATUS_TIP_LINES, inactivityWarning, lastSeenLabel, laufzeitLabel, planTipLines } from './saasLabels'
import { useDeleteWorkshop, useResetChefPassword, useUpdateWorkshop } from './useAdmin'

/**
 * Eine Werkstatt-Zeile in der App-Verwaltung: Status-Chip (mit Erklär-Tooltip), Laufzeit, MRR, letzter
 * Login + Churn-Warnung; Aktionen: Tarif ändern, Lizenz sperren/freigeben/kündigen, Support-Zugriff
 * („Als Werkstatt einloggen"), Chef-Passwort zurücksetzen, löschen (DSGVO-Tiefenlöschung).
 * Jede Aktion erzwingt der Server zusätzlich über is_superadmin.
 */
export function AdminWorkshopRow({ workshop }: { workshop: AdminWorkshopDTO }) {
  const update = useUpdateWorkshop()
  const remove = useDeleteWorkshop()
  const resetPw = useResetChefPassword()

  const status = workshop.status
  const overLimit = workshop.userCount > workshop.userLimit
  const mrr = status === 'active' ? planPriceCents(workshop.plan) : 0
  const churnWarn = inactivityWarning(status, workshop.lastSeenAt)

  function changePlan(plan: string) {
    if (plan !== workshop.plan) update.mutate({ id: workshop.id, body: { plan } })
  }
  function setStatus(next: WorkshopStatus) {
    if (next === 'canceled' && !window.confirm(
      `Lizenz von „${workshop.name}" wirklich KÜNDIGEN?\nDer Zugang geht dauerhaft zu; alle Daten bleiben für die gesetzlichen Fristen erhalten.`,
    )) return
    update.mutate({ id: workshop.id, body: { status: next } })
  }
  function reissueActivation() {
    if (
      !window.confirm(
        `Neuen Aktivierungscode für den Chef von „${workshop.name}" ausstellen?\nEin zuvor ausgegebener Code wird damit ungültig; der Chef setzt sein Passwort selbst.`,
      )
    )
      return
    resetPw.mutate({ id: workshop.id, body: {} })
  }
  function confirmDelete() {
    if (
      window.confirm(
        `Werkstatt „${workshop.name}" und ALLE ihre Daten unwiderruflich löschen?\nDas kann nicht rückgängig gemacht werden.`,
      )
    ) {
      remove.mutate(workshop.id)
    }
  }

  const actionError = (update.error || remove.error || resetPw.error) as Error | null

  return (
    <div className={`wf-card wf-adminrow${status !== 'active' ? ' wf-adminrow--inactive' : ''}`}>
      <div className="wf-adminrow__main">
        <div className="wf-adminrow__title">
          <span className="wf-adminrow__id muted">#{workshop.id}</span>
          <Link to={`/admin/workshops/${workshop.id}`} className="wf-adminrow__open">
            {workshop.name}
          </Link>
          <span className={`wf-chip wf-chip--static ${STATUS_CHIP[status]}`}>{STATUS_LABELS[status]}</span>
          <InfoTip title="Lizenz-Status" purpose="Was der Status für die Werkstatt bedeutet:" lines={STATUS_TIP_LINES} />
          {churnWarn && <span className="wf-chip wf-chip--static wf-chip--warn">⚠️ {churnWarn}</span>}
        </div>
        <div className="wf-adminrow__meta">
          <span>{laufzeitLabel(workshop.createdAt)}</span>
          <span>{mrr > 0 ? `${formatEur(mrr)}/Mon.` : 'kein Umsatz'}</span>
          <span className="muted">letzter Login: {lastSeenLabel(workshop.lastSeenAt)}</span>
          <span className={overLimit ? 'wf-overlimit' : undefined}>
            {workshop.userCount} / {workshop.userLimit} Nutzer
          </span>
          {workshop.chefUsername && <span className="muted">Chef: {workshop.chefUsername}</span>}
        </div>
      </div>

      <div className="wf-adminrow__controls">
        <Link to={`/admin/workshops/${workshop.id}`} className="wf-btn wf-btn--sm wf-btn--primary">
          Verwalten ›
        </Link>
        <button
          type="button"
          className="wf-btn wf-btn--sm"
          onClick={() => startImpersonation({ id: workshop.id, name: workshop.name })}
          title="Support-Modus: du arbeitest als diese Werkstatt — jede Änderung wird protokolliert"
        >
          Als Werkstatt einloggen
        </button>
        <label className="wf-adminrow__plan">
          <span className="wf-label">
            Tarif
            <InfoTip title="Tarife" purpose="Monatspreis und Nutzer-Limit je Tarif (zentrale Preis-Wahrheit in @wf/shared):" lines={planTipLines()} />
          </span>
          <select className="wf-input" value={workshop.plan ?? 'test'} disabled={update.isPending} onChange={(e) => changePlan(e.target.value)}>
            {KNOWN_PLANS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        {status === 'active' && (
          <button type="button" className="wf-btn wf-btn--sm wf-btn--ghost" disabled={update.isPending} onClick={() => setStatus('suspended')}>
            Lizenz sperren
          </button>
        )}
        {status === 'suspended' && (
          <button type="button" className="wf-btn wf-btn--sm wf-btn--primary" disabled={update.isPending} onClick={() => setStatus('active')}>
            Lizenz freigeben
          </button>
        )}
        {status !== 'canceled' ? (
          <button type="button" className="wf-btn wf-btn--sm wf-btn--ghost" disabled={update.isPending} onClick={() => setStatus('canceled')}>
            Kündigen
          </button>
        ) : (
          <button type="button" className="wf-btn wf-btn--sm wf-btn--primary" disabled={update.isPending} onClick={() => setStatus('active')}>
            Reaktivieren
          </button>
        )}
        <button
          type="button"
          className="wf-btn wf-btn--sm wf-btn--ghost"
          onClick={reissueActivation}
          disabled={!workshop.chefUsername || resetPw.isPending}
          title="Neuen Aktivierungscode für den Chef ausstellen (der Chef setzt sein Passwort selbst)"
        >
          Chef-Zugang zurücksetzen
        </button>
        <button type="button" className="wf-btn wf-btn--sm wf-btn--danger" disabled={remove.isPending} onClick={confirmDelete}>
          Löschen
        </button>
      </div>

      {resetPw.data && (
        <ChefActivationReveal
          code={resetPw.data.activation.code}
          chefUsername={resetPw.data.chefUsername}
          heading="🔑 Neuer Aktivierungscode"
        />
      )}
      {overLimit && (
        <div className="wf-adminrow__note wf-overlimit">
          Mehr aktive Nutzer als der Tarif erlaubt — keine Neuanlage möglich, bis der Tarif passt.
        </div>
      )}
      {actionError && (
        <div className="error" role="alert">
          {actionError.message}
        </div>
      )}
    </div>
  )
}
